import { ingredientById } from "../data/ingredients";
import { recipeById, recipeFamilies } from "../data/recipes";
import { fillRecipeSlots } from "./instantiateRecipe";
import { matchSelectedIngredients } from "./matchSlots";
import {
  EMPTY_SELECTION,
  type Candidate,
  type RecipeFamily,
  type SelectionState,
} from "./types";

function makeCandidate(
  recipe: RecipeFamily,
  selection: SelectionState,
): Candidate | null {
  const match = matchSelectedIngredients(recipe, selection.ingredientIds);
  if (!match) return null;

  const slotById = new Map(recipe.slots.map((s) => [s.id, s]));
  const candidate: Candidate = {
    recipeId: recipe.id,
    slotAssignment: match.assignment,
    canonicalMatchCount: match.canonicalMatchCount,
    matchedIngredientCount: match.matchedIngredientCount,
    missingRequiredSlotCount: recipe.slots.filter((slot) => {
      if (!slot.required || match.assignment[slot.id]) return false;
      const canonical = ingredientById.get(slot.canonicalIngredientId);
      return !canonical?.assumedStaple;
    }).length,
    selectedSubstitutionIds: Object.entries(match.assignment)
      .filter(
        ([slotId, ingredientId]) =>
          slotById.get(slotId)?.canonicalIngredientId !== ingredientId,
      )
      .map(([, ingredientId]) => ingredientId)
      .sort(),
  };

  return fillRecipeSlots(recipe, candidate, {}, selection.veganOnly)
    ? candidate
    : null;
}

export function getCandidatesFromRecipes(
  selection: SelectionState,
  recipes: readonly RecipeFamily[],
): Candidate[] {
  return recipes
    .filter(
      (recipe) =>
        (selection.foodTypeIds.length === 0 ||
          selection.foodTypeIds.includes(recipe.foodType)) &&
        (selection.cookingLocationIds.length === 0 ||
          selection.cookingLocationIds.includes(recipe.primaryCookingLocation)),
    )
    .flatMap((recipe) => {
      const candidate = makeCandidate(recipe, selection);
      return candidate ? [{ candidate, recipe }] : [];
    })
    .sort(
      (a, b) =>
        b.candidate.matchedIngredientCount -
          a.candidate.matchedIngredientCount ||
        b.candidate.canonicalMatchCount - a.candidate.canonicalMatchCount ||
        a.candidate.missingRequiredSlotCount -
          b.candidate.missingRequiredSlotCount ||
        a.recipe.totalMinutes - b.recipe.totalMinutes ||
        b.recipe.editorialPriority - a.recipe.editorialPriority ||
        a.recipe.id.localeCompare(b.recipe.id),
    )
    .map(({ candidate }) => candidate);
}

export function getCandidates(selection: SelectionState): Candidate[] {
  return getCandidatesFromRecipes(selection, recipeFamilies);
}

export function getStarterCandidates(limit = 6): Candidate[] {
  const remaining = [...getCandidates(EMPTY_SELECTION)];
  const selected: Candidate[] = [];
  const usedFoodTypes = new Set<string>();
  const usedLocations = new Set<string>();

  while (selected.length < limit && remaining.length > 0) {
    let bestIndex = 0;
    let bestDiversityScore = -1;
    for (const [index, candidate] of remaining.entries()) {
      const recipe = recipeById.get(candidate.recipeId);
      if (!recipe) continue;
      const diversityScore =
        Number(!usedFoodTypes.has(recipe.foodType)) * 2 +
        Number(!usedLocations.has(recipe.primaryCookingLocation));
      if (diversityScore > bestDiversityScore) {
        bestIndex = index;
        bestDiversityScore = diversityScore;
      }
    }

    const [candidate] = remaining.splice(bestIndex, 1);
    const recipe = recipeById.get(candidate.recipeId);
    if (!recipe) continue;
    selected.push(candidate);
    usedFoodTypes.add(recipe.foodType);
    usedLocations.add(recipe.primaryCookingLocation);
  }

  return selected;
}

export function candidateForRecipe(
  recipeId: string,
  selection: SelectionState,
): Candidate | null {
  const recipe = recipeById.get(recipeId);
  if (!recipe) return null;
  if (
    selection.foodTypeIds.length > 0 &&
    !selection.foodTypeIds.includes(recipe.foodType)
  )
    return null;
  if (
    selection.cookingLocationIds.length > 0 &&
    !selection.cookingLocationIds.includes(recipe.primaryCookingLocation)
  )
    return null;
  return makeCandidate(recipe, selection);
}

export function withIngredient(
  selection: SelectionState,
  ingredientId: string,
): SelectionState {
  if (selection.ingredientIds.includes(ingredientId)) return selection;
  return {
    ...selection,
    ingredientIds: [...selection.ingredientIds, ingredientId].sort(),
  };
}

export function selectionFromState(state: SelectionState): SelectionState {
  return {
    foodTypeIds: [...state.foodTypeIds].sort(),
    cookingLocationIds: [...state.cookingLocationIds].sort(),
    ingredientIds: [...state.ingredientIds].sort(),
    veganOnly: state.veganOnly,
  };
}

export function activeSelectionCount(selection: SelectionState): number {
  return (
    selection.ingredientIds.length +
    selection.foodTypeIds.length +
    selection.cookingLocationIds.length +
    Number(selection.veganOnly)
  );
}
